import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';
import {
    Box, Card, Stack, TextField, MenuItem,
    Button, Grid, LinearProgress, Alert, ToggleButton, ToggleButtonGroup,
    Drawer, Typography, Divider, IconButton, Tooltip, Chip, Snackbar
} from "@mui/material";
import {
    FileDownload as ExportIcon,
    Refresh as RefreshIcon,
    EditNote as OverrideIcon,
    Close as CloseIcon,
    CheckCircle as ApproveIcon,
    Cancel as RejectIcon,
    Undo as RevokeIcon
} from "@mui/icons-material";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import AppHeader from '../../components/AppHeader';
import { buildHierarchicalList } from '../../components/staff/constants';

const getDescendantNodeIds = (nodeId, nodesList) => {
    const ids = [nodeId];
    const traverse = (currentId) => {
        for (const node of nodesList) {
            if (node.parent_id === currentId) {
                ids.push(node.id);
                traverse(node.id);
            }
        }
    };
    traverse(nodeId);
    return ids;
};

const OVERRIDE_COMPONENTS = [
    { code: 'TOTAL_OT_MINS', label: 'Total OT', unit: 'MINUTES', field: 'total_ot_mins' },
    { code: 'NORMAL_OT_MINS', label: 'Normal OT', unit: 'MINUTES', field: 'normal_ot_mins' },
    { code: 'REST_OT_MINS', label: 'Rest Day OT', unit: 'MINUTES', field: 'rest_ot_mins' },
    { code: 'HOLIDAY_OT_MINS', label: 'Holiday OT', unit: 'MINUTES', field: 'holiday_ot_mins' },
    { code: 'EXTENDED_OT_MINS', label: 'Extended OT', unit: 'MINUTES', field: 'extended_ot_mins' },
    { code: 'MEAL_ALLOWANCE_DAYS', label: 'Meal Allowance Days', unit: 'DAYS', field: 'meal_allowance_days' },
    { code: 'TRANSPORT_ALLOWANCE_DAYS', label: 'Transport Allowance Days', unit: 'DAYS', field: 'transport_allowance_days' },
    { code: 'EARLY_ARRIVAL_DAYS', label: 'Early Arrival Days', unit: 'DAYS', field: 'early_arrival_eligible_days' },
    { code: 'PERFECT_ATTENDANCE_ELIGIBLE', label: 'Perfect Attendance Eligible', unit: 'BOOLEAN', field: 'is_pai_eligible' },
    { code: 'PAID_ABSENCE_DAYS', label: 'Paid Absence Days', unit: 'DAYS', field: 'paid_absence_days' },
    { code: 'UNPAID_ABSENCE_DAYS', label: 'Unpaid Absence Days', unit: 'DAYS', field: 'unpaid_absence_days' },
    { code: 'LATE_EARLY_PENALTY_AMOUNT', label: 'Late/Early Penalty Amount', unit: 'AMOUNT', field: 'total_penalties' }
];

const componentMap = OVERRIDE_COMPONENTS.reduce((acc, item) => ({ ...acc, [item.code]: item }), {});

const valueForComponent = (row, config) => {
    if (!row || !config?.field) return null;
    if (config.unit === 'BOOLEAN') return row[config.field] ? 1 : 0;
    const value = Number(row[config.field] || 0);
    return Number.isFinite(value) ? value : null;
};

const formatOverrideValue = (value, unit) => {
    if (value === null || value === undefined || value === '') return '-';
    if (unit === 'BOOLEAN') return Number(value) === 1 ? 'Eligible' : 'No';
    if (unit === 'MINUTES') return `${Number(value || 0).toLocaleString()} m`;
    if (unit === 'DAYS') return `${Number(value || 0).toFixed(2)} d`;
    if (unit === 'AMOUNT') return `${Number(value || 0).toLocaleString()} MMK`;
    return Number(value || 0).toLocaleString();
};

const statusColor = (status) => {
    if (status === 'APPROVED') return 'success';
    if (status === 'PENDING') return 'warning';
    if (status === 'REJECTED') return 'error';
    if (status === 'REVOKED') return 'default';
    return 'default';
};

const MonthlyReview = ({ setIsLoggedIn }) => {
    const gridRef = useRef();
    const [loading, setLoading] = useState(false);
    const [rowData, setRowData] = useState([]);
    const [filters, setFilters] = useState({
        month: new Date().getMonth() + 1, // 1-12
        year: new Date().getFullYear(),
    });
    const [searchText, setSearchText] = useState('');
    const [selectedBranch, setSelectedBranch] = useState('');
    const [selectedOrgNode, setSelectedOrgNode] = useState('');
    const [otDisplayUnit, setOtDisplayUnit] = useState('minutes');
    const [branches, setBranches] = useState([]);
    const [orgNodes, setOrgNodes] = useState([]);
    const [selectedStaff, setSelectedStaff] = useState(null);
    const [overrideRows, setOverrideRows] = useState([]);
    const [overrideLoading, setOverrideLoading] = useState(false);
    const [overrideSaving, setOverrideSaving] = useState(false);
    const [overrideForm, setOverrideForm] = useState({
        component_code: 'TOTAL_OT_MINS',
        override_value: '',
        reason: ''
    });
    const [message, setMessage] = useState({ open: false, severity: 'success', text: '' });

    const months = [
        { value: 1, label: 'January' }, { value: 2, label: 'February' },
        { value: 3, label: 'March' }, { value: 4, label: 'April' },
        { value: 5, label: 'May' }, { value: 6, label: 'June' },
        { value: 7, label: 'July' }, { value: 8, label: 'August' },
        { value: 9, label: 'September' }, { value: 10, label: 'October' },
        { value: 11, label: 'November' }, { value: 12, label: 'December' }
    ];

    const currentYear = new Date().getFullYear();
    const years = [currentYear - 1, currentYear, currentYear + 1];

    const formatOtValue = useCallback((value) => {
        const minutes = Number(value || 0);
        if (!minutes) return '-';
        if (otDisplayUnit === 'hours') {
            return `${(minutes / 60).toFixed(2)} h`;
        }
        return `${minutes.toLocaleString()} m`;
    }, [otDisplayUnit]);

    const requestConfig = useCallback(() => {
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');
        const tenant = localStorage.getItem('tenant') || '';
        return {
            headers: {
                Authorization: `Bearer ${token}`,
                'X-Tenant': tenant
            },
            withCredentials: true
        };
    }, []);

    const showMessage = useCallback((severity, text) => {
        setMessage({ open: true, severity, text });
    }, []);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL1}/sm/payroll/monthly-attendance-summary`, {
                params: filters,
                ...requestConfig()
            });
            if (response.data.success) {
                setRowData(response.data.data);
            }
        } catch (error) {
            console.error("Error fetching summary:", error);
            showMessage('error', error.response?.data?.message || 'Failed to load monthly summary');
        } finally {
            setLoading(false);
        }
    }, [filters, requestConfig, showMessage]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Fetch branches and departments list on load
    useEffect(() => {
        const fetchFilters = async () => {
            try {
                const token = sessionStorage.getItem('token') || localStorage.getItem('token');
                const tenant = localStorage.getItem('tenant') || '';
                const config = {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'X-Tenant': tenant
                    },
                    withCredentials: true
                };
                const [branchRes, nodeRes] = await Promise.all([
                    axios.get(`${process.env.REACT_APP_API_URL1}/som/branches`, config),
                    axios.get(`${process.env.REACT_APP_API_URL1}/som/nodes`, config)
                ]);
                if (branchRes.data?.success) setBranches(branchRes.data.data);
                if (nodeRes.data?.success) setOrgNodes(buildHierarchicalList(nodeRes.data.data));
            } catch (error) {
                console.error("Error fetching filter options:", error);
            }
        };
        fetchFilters();
    }, []);

    const branchMap = useMemo(() => {
        return branches.reduce((acc, b) => ({ ...acc, [b.id]: b.branch_name }), {});
    }, [branches]);

    const orgNodeMap = useMemo(() => {
        return orgNodes.reduce((acc, n) => ({ ...acc, [n.id]: `${n.node_name} (${n.node_code})` }), {});
    }, [orgNodes]);

    const filteredRowData = useMemo(() => {
        return rowData.filter(row => {
            // Search Text filter
            if (searchText) {
                const term = searchText.toLowerCase().trim();
                const nameMatch = row.full_name ? row.full_name.toLowerCase().includes(term) : false;
                const empIdMatch = row.employee_number ? row.employee_number.toLowerCase().includes(term) : false;
                const codeMatch = row.staff_code ? row.staff_code.toLowerCase().includes(term) : false;
                if (!nameMatch && !empIdMatch && !codeMatch) return false;
            }
            // Branch filter
            if (selectedBranch) {
                if (row.branch_id !== Number(selectedBranch)) return false;
            }
            // SOM/Org Node filter
            if (selectedOrgNode) {
                const targetNodeId = Number(selectedOrgNode);
                const allowedNodeIds = getDescendantNodeIds(targetNodeId, orgNodes);
                if (!allowedNodeIds.includes(row.org_node_id)) return false;
            }
            return true;
        });
    }, [rowData, searchText, selectedBranch, selectedOrgNode, orgNodes]);

    const notReadyStaffCount = useMemo(() => {
        return filteredRowData.filter(row => row.is_attendance_ready === false || row.missing_days > 0 || row.incomplete_days > 0).length;
    }, [filteredRowData]);

    const onBtnExport = useCallback(() => {
        if (gridRef.current?.api) {
            gridRef.current.api.exportDataAsCsv({
                fileName: `monthly_attendance_review_${filters.year}_${filters.month}.csv`
            });
        }
    }, [filters]);

    const fetchOverrides = useCallback(async (staff) => {
        if (!staff) return;
        setOverrideLoading(true);
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL1}/sm/payroll/input-overrides`, {
                params: {
                    month: filters.month,
                    year: filters.year,
                    staff_id: staff.staff_id
                },
                ...requestConfig()
            });
            setOverrideRows(response.data?.data || []);
        } catch (error) {
            console.error('Error fetching overrides:', error);
            showMessage('error', error.response?.data?.message || 'Failed to load overrides');
        } finally {
            setOverrideLoading(false);
        }
    }, [filters, requestConfig, showMessage]);

    const openOverrideDrawer = useCallback((staff) => {
        setSelectedStaff(staff);
        setOverrideForm({ component_code: 'TOTAL_OT_MINS', override_value: '', reason: '' });
        fetchOverrides(staff);
    }, [fetchOverrides]);

    const closeOverrideDrawer = useCallback(() => {
        setSelectedStaff(null);
        setOverrideRows([]);
        setOverrideForm({ component_code: 'TOTAL_OT_MINS', override_value: '', reason: '' });
    }, []);

    const activeOverrides = useMemo(() => {
        return overrideRows.reduce((acc, row) => {
            if (['PENDING', 'APPROVED'].includes(row.status)) acc[row.component_code] = row;
            return acc;
        }, {});
    }, [overrideRows]);

    const selectedComponent = componentMap[overrideForm.component_code] || OVERRIDE_COMPONENTS[0];

    const saveOverride = useCallback(async () => {
        if (!selectedStaff) return;
        const component = componentMap[overrideForm.component_code];
        const calculatedValue = valueForComponent(selectedStaff, component);
        if (overrideForm.override_value === '' || !overrideForm.reason.trim()) {
            showMessage('error', 'Override value and reason are required');
            return;
        }
        setOverrideSaving(true);
        try {
            await axios.post(`${process.env.REACT_APP_API_URL1}/sm/payroll/input-overrides`, {
                month: filters.month,
                year: filters.year,
                staff_id: selectedStaff.staff_id,
                component_code: overrideForm.component_code,
                calculated_value: calculatedValue,
                override_value: Number(overrideForm.override_value),
                reason: overrideForm.reason.trim()
            }, requestConfig());
            showMessage('success', 'Override submitted for approval');
            setOverrideForm({ component_code: overrideForm.component_code, override_value: '', reason: '' });
            await fetchOverrides(selectedStaff);
        } catch (error) {
            console.error('Save override error:', error);
            showMessage('error', error.response?.data?.message || 'Failed to save override');
        } finally {
            setOverrideSaving(false);
        }
    }, [fetchOverrides, filters, overrideForm, requestConfig, selectedStaff, showMessage]);

    const actOnOverride = useCallback(async (row, action) => {
        let body = {};
        if (action === 'reject' || action === 'revoke') {
            const reason = window.prompt(action === 'reject' ? 'Rejection reason' : 'Revoke reason');
            if (!reason?.trim()) return;
            body.reason = reason.trim();
        }
        setOverrideSaving(true);
        try {
            await axios.post(`${process.env.REACT_APP_API_URL1}/sm/payroll/input-overrides/${row.id}/${action}`, body, requestConfig());
            showMessage('success', `Override ${action === 'approve' ? 'approved' : action === 'reject' ? 'rejected' : 'revoked'}`);
            await fetchOverrides(selectedStaff);
            await fetchData();
        } catch (error) {
            console.error(`Override ${action} error:`, error);
            showMessage('error', error.response?.data?.message || `Failed to ${action} override`);
        } finally {
            setOverrideSaving(false);
        }
    }, [fetchData, fetchOverrides, requestConfig, selectedStaff, showMessage]);

    const columnDefs = useMemo(() => [
        {
            headerName: "Action",
            pinned: 'left',
            width: 95,
            sortable: false,
            filter: false,
            cellRenderer: params => (
                <Tooltip title="Payroll input overrides">
                    <IconButton size="small" onClick={() => openOverrideDrawer(params.data)}>
                        <OverrideIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            )
        },
        {
            headerName: "Readiness",
            field: "missing_days",
            pinned: 'left',
            width: 140,
            cellRenderer: params => {
                const missing = params.value || 0;
                const incomplete = Number(params.data?.incomplete_days || 0);
                const unresolved = Number(params.data?.unresolved_absence_days || 0);
                if (missing > 0) {
                    return <span style={{ color: '#d32f2f', fontWeight: 'bold' }}>⚠️ Missing {missing}d</span>;
                }
                if (incomplete > 0) {
                    return <span style={{ color: '#ed6c02', fontWeight: 'bold' }}>⚠️ Incomplete {incomplete}d</span>;
                }
                if (unresolved > 0) {
                    return <span style={{ color: '#ed6c02', fontWeight: 'bold' }}>⚠️ Unresolved {unresolved}d</span>;
                }
                return <span style={{ color: '#2e7d32' }}>✅ Ready</span>;
            }
        },
        {
            headerName: "Staff Info",
            pinned: 'left',
            children: [
                { field: "employee_number", headerName: "Emp ID", width: 120 },
                { field: "full_name", headerName: "Name", width: 180 },
            ]
        },
        {
            headerName: "Org Structure",
            children: [
                {
                    headerName: "Branch",
                    field: "branch_id",
                    width: 140,
                    valueFormatter: params => branchMap[params.value] || '-'
                },
                {
                    headerName: "Department",
                    field: "org_node_id",
                    width: 150,
                    valueFormatter: params => orgNodeMap[params.value] || '-'
                }
            ]
        },
        {
            headerName: "Attendance",
            children: [
                { field: "expected_days", headerName: "Expected", width: 100 },
                { field: "processed_days", headerName: "Processed", width: 110 },
                { field: "days_present", headerName: "Present", width: 100 },
                {
                    field: "total_late_mins",
                    headerName: "Late (m)",
                    width: 100,
                    cellStyle: params => params.value > 0 ? { color: '#d32f2f', fontWeight: 'bold' } : null
                },
                { field: "total_ot_mins", headerName: "Total OT", width: 120, valueFormatter: params => formatOtValue(params.value) },
                { field: "normal_ot_mins", headerName: "Normal OT", width: 120, valueFormatter: params => formatOtValue(params.value) },
                { field: "rest_ot_mins", headerName: "Rest OT", width: 110, valueFormatter: params => formatOtValue(params.value) },
                { field: "holiday_ot_mins", headerName: "Holiday OT", width: 120, valueFormatter: params => formatOtValue(params.value) },
                { field: "extended_ot_mins", headerName: "Extended OT", width: 130, valueFormatter: params => formatOtValue(params.value) },
            ]
        },
        {
            headerName: "Variable Allowances (MMK)",
            children: [
                { field: "meal_allowance_eligible_days", headerName: "Meal Qty", width: 90 },
                { field: "total_meal_allw", headerName: "Meal", width: 100 },
                { field: "transport_allowance_eligible_days", headerName: "Trans Qty", width: 100 },
                { field: "total_transport_allw", headerName: "Trans", width: 100 },
                { field: "ot_allowance_eligible_days", headerName: "OT Meal Qty", width: 110 },
                { field: "total_ot_allw", headerName: "OT Meal", width: 100 },
                { field: "special_allowance_eligible_days", headerName: "Spec Qty", width: 100 },
                { field: "total_special_allw", headerName: "Spec", width: 100 },
            ]
        },
        {
            headerName: "Incentive Basis",
            children: [
                {
                    field: "early_arrival_eligible_days",
                    headerName: "Early Arrival Days",
                    width: 150,
                    valueFormatter: params => Number(params.value || 0) > 0 ? Number(params.value).toLocaleString() : '-'
                },
                {
                    field: "total_early_arrival_allw",
                    headerName: "Early Arrival MMK",
                    width: 130,
                    cellStyle: params => params.value > 0 ? { color: '#0d9488', fontWeight: 'bold' } : null,
                    valueFormatter: params => params.value > 0 ? params.value.toLocaleString() : '-'
                },
                {
                    field: "is_pai_eligible",
                    headerName: "Perfect Att.",
                    width: 120,
                    cellRenderer: params => params.value ? 'Eligible' : 'No',
                    cellStyle: { textAlign: 'center' }
                },
                {
                    field: "pai_allowance_amount",
                    headerName: "Perfect Att. MMK",
                    width: 140,
                    cellStyle: params => params.value > 0 ? { color: '#0d9488', fontWeight: 'bold' } : null,
                    valueFormatter: params => params.value > 0 ? params.value.toLocaleString() : '-'
                },
            ]
        },

        {
            headerName: "Deductions",
            children: [
                {
                    field: "total_penalties",
                    headerName: "Late Penalties",
                    width: 140,
                    cellStyle: params => params.value > 0 ? { color: '#d32f2f', fontWeight: 'bold' } : null
                },
                { field: "violation_count", headerName: "Infractions", width: 110 },
            ]
        },
        {
            headerName: "Absence Resolutions (Days)",
            children: [
                { 
                    field: "unresolved_absence_days", 
                    headerName: "Unresolved", 
                    width: 110, 
                    cellStyle: params => params.value > 0 ? { color: '#ed6c02', fontWeight: 'bold' } : null 
                },
                { 
                    field: "paid_absence_days", 
                    headerName: "Paid", 
                    width: 90,
                    valueFormatter: params => params.value > 0 ? Number(params.value).toFixed(2) : '-'
                },
                { 
                    field: "unpaid_absence_days", 
                    headerName: "Unpaid", 
                    width: 90,
                    valueFormatter: params => params.value > 0 ? Number(params.value).toFixed(2) : '-'
                },
            ]
        },
        {
            headerName: "Leave Summary (Days)",
            children: [
                { headerName: "AL", valueGetter: params => params.data.leaves?.AL || 0, width: 70 },
                { headerName: "SL", valueGetter: params => params.data.leaves?.SL || params.data.leaves?.MC || 0, width: 70 },
                { headerName: "CL", valueGetter: params => params.data.leaves?.CL || 0, width: 70 },
                { headerName: "UL", valueGetter: params => params.data.leaves?.UL || params.data.leaves?.UP || 0, width: 70 },
            ]
        }
    ], [branchMap, formatOtValue, openOverrideDrawer, orgNodeMap]);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: '#f4f7fe' }}>
            <AppHeader title="Monthly Attendance Review" setIsLoggedIn={setIsLoggedIn} />

            <Box sx={{ p: 3, flexGrow: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                <Card sx={{ p: 2, mb: 3, borderRadius: 3, boxShadow: '0 4px 20px 0 rgba(0,0,0,0.05)' }}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={2} md={1.5}>
                            <TextField
                                fullWidth
                                select
                                label="Month"
                                value={filters.month}
                                onChange={(e) => setFilters({ ...filters, month: e.target.value })}
                                size="small"
                            >
                                {months.map((m) => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={2} md={1.2}>
                            <TextField
                                fullWidth
                                select
                                label="Year"
                                value={filters.year}
                                onChange={(e) => setFilters({ ...filters, year: e.target.value })}
                                size="small"
                            >
                                {years.map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={3} md={2.5}>
                            <TextField
                                fullWidth
                                label="Search Staff"
                                placeholder="Name, ID or Code"
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12} sm={3} md={2}>
                            <TextField
                                fullWidth
                                select
                                label="Branch"
                                value={selectedBranch}
                                onChange={(e) => setSelectedBranch(e.target.value)}
                                size="small"
                            >
                                <MenuItem value="">All Branches</MenuItem>
                                {branches.map((b) => <MenuItem key={b.id} value={b.id}>{b.branch_name}</MenuItem>)}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={3} md={2.3}>
                            <TextField
                                fullWidth
                                select
                                label="SOM / Org Node"
                                value={selectedOrgNode}
                                onChange={(e) => setSelectedOrgNode(e.target.value)}
                                size="small"
                            >
                                <MenuItem value="">All Departments</MenuItem>
                                {orgNodes.map((n) => (
                                    <MenuItem key={n.id} value={n.id} sx={{ pl: 2 + (n._depth || 0) * 2 }}>
                                        {n._depth > 0 && <span style={{ color: '#94a3b8', marginRight: 4 }}>{'└'}</span>}
                                        {n.node_name}{n.level_type ? ` (${n.level_type})` : ''} ({n.node_code})
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} md={'auto'} sx={{ ml: 'auto' }}>
                            <Stack direction="row" spacing={1}>
                                <Button
                                    variant="outlined"
                                    startIcon={<RefreshIcon />}
                                    onClick={fetchData}
                                    sx={{ borderRadius: 2, textTransform: 'none' }}
                                >
                                    Refresh
                                </Button>
                                <Button
                                    variant="contained"
                                    startIcon={<ExportIcon />}
                                    onClick={onBtnExport}
                                    sx={{
                                        borderRadius: 2,
                                        textTransform: 'none',
                                        bgcolor: '#1a237e',
                                        '&:hover': { bgcolor: '#0d47a1' }
                                    }}
                                >
                                    Export CSV
                                </Button>
                            </Stack>
                        </Grid>
                    </Grid>
                </Card>

                {notReadyStaffCount > 0 && (
                    <Alert severity="warning" sx={{ mb: 3, borderRadius: 2 }}>
                        <strong>Warning:</strong> {notReadyStaffCount} staff members have missing or incomplete attendance records. Please ask HR to run "Process Attendance Daily" and resolve incomplete records before finalizing payroll. Perfect Attendance Incentives for these staff have been disabled automatically.
                    </Alert>
                )}

                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ fontSize: '14px', fontWeight: 600, color: '#475569' }}>
                        Showing {filteredRowData.length} of {rowData.length} staff members
                    </Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Box sx={{ fontSize: 13, fontWeight: 600, color: '#475569' }}>OT Display</Box>
                        <ToggleButtonGroup
                            exclusive
                            size="small"
                            value={otDisplayUnit}
                            onChange={(event, value) => value && setOtDisplayUnit(value)}
                        >
                            <ToggleButton value="minutes" sx={{ textTransform: 'none', px: 1.5 }}>Minutes</ToggleButton>
                            <ToggleButton value="hours" sx={{ textTransform: 'none', px: 1.5 }}>Hours</ToggleButton>
                        </ToggleButtonGroup>
                    </Stack>
                </Box>

                {loading && <LinearProgress sx={{ mb: 1, borderRadius: 1 }} />}
                <Card sx={{ flexGrow: 1, borderRadius: 3, overflow: 'hidden', boxShadow: '0 4px 20px 0 rgba(0,0,0,0.05)' }}>
                    <Box className="ag-theme-quartz" sx={{ height: '100%', width: '100%' }}>
                        <AgGridReact
                            ref={gridRef}
                            rowData={filteredRowData}
                            columnDefs={columnDefs}
                            defaultColDef={{
                                sortable: true,
                                filter: true,
                                resizable: true
                            }}
                            pagination={true}
                            paginationPageSize={20}
                        />
                    </Box>
                </Card>
            </Box>

            <Drawer
                anchor="right"
                open={!!selectedStaff}
                onClose={closeOverrideDrawer}
                PaperProps={{ sx: { width: { xs: '100%', sm: 560 }, borderLeft: '1px solid #e2e8f0' } }}
            >
                <Box sx={{ p: 3, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                    <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
                        <Box>
                            <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a' }}>
                                Payroll Input Overrides
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>
                                {selectedStaff?.employee_number || selectedStaff?.staff_code} · {selectedStaff?.full_name}
                            </Typography>
                        </Box>
                        <IconButton onClick={closeOverrideDrawer}>
                            <CloseIcon />
                        </IconButton>
                    </Stack>
                </Box>

                <Box sx={{ p: 3 }}>
                    <Stack spacing={2}>
                        <TextField
                            select
                            label="Component"
                            size="small"
                            value={overrideForm.component_code}
                            onChange={(e) => setOverrideForm({ ...overrideForm, component_code: e.target.value, override_value: '' })}
                        >
                            {OVERRIDE_COMPONENTS.map(component => (
                                <MenuItem key={component.code} value={component.code}>{component.label}</MenuItem>
                            ))}
                        </TextField>

                        <Grid container spacing={1.5}>
                            <Grid item xs={4}>
                                <Box sx={{ p: 1.5, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 2 }}>
                                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700 }}>Calculated</Typography>
                                    <Typography sx={{ fontWeight: 800, color: '#0f172a' }}>
                                        {formatOverrideValue(valueForComponent(selectedStaff, selectedComponent), selectedComponent.unit)}
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={4}>
                                <Box sx={{ p: 1.5, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 2 }}>
                                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700 }}>Active Override</Typography>
                                    <Typography sx={{ fontWeight: 800, color: '#0f172a' }}>
                                        {formatOverrideValue(activeOverrides[overrideForm.component_code]?.override_value, selectedComponent.unit)}
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={4}>
                                <Box sx={{ p: 1.5, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 2 }}>
                                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700 }}>Payroll Final</Typography>
                                    <Typography sx={{ fontWeight: 800, color: '#0f172a' }}>
                                        {formatOverrideValue(
                                            activeOverrides[overrideForm.component_code]?.status === 'APPROVED'
                                                ? activeOverrides[overrideForm.component_code]?.override_value
                                                : valueForComponent(selectedStaff, selectedComponent),
                                            selectedComponent.unit
                                        )}
                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>

                        {selectedComponent.unit === 'BOOLEAN' ? (
                            <TextField
                                select
                                label="Override Value"
                                size="small"
                                value={overrideForm.override_value}
                                onChange={(e) => setOverrideForm({ ...overrideForm, override_value: e.target.value })}
                            >
                                <MenuItem value={1}>Eligible</MenuItem>
                                <MenuItem value={0}>No</MenuItem>
                            </TextField>
                        ) : (
                            <TextField
                                label={`Override Value (${selectedComponent.unit})`}
                                size="small"
                                type="number"
                                value={overrideForm.override_value}
                                onChange={(e) => setOverrideForm({ ...overrideForm, override_value: e.target.value })}
                            />
                        )}

                        <TextField
                            label="Reason"
                            size="small"
                            multiline
                            minRows={2}
                            value={overrideForm.reason}
                            onChange={(e) => setOverrideForm({ ...overrideForm, reason: e.target.value })}
                        />

                        <Button
                            variant="contained"
                            onClick={saveOverride}
                            disabled={overrideSaving}
                            sx={{ alignSelf: 'flex-start', textTransform: 'none', bgcolor: '#1a237e' }}
                        >
                            Submit Override
                        </Button>
                    </Stack>

                    <Divider sx={{ my: 3 }} />

                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                        <Typography sx={{ fontWeight: 800, color: '#0f172a' }}>Override History</Typography>
                        {overrideLoading && <Typography variant="caption" sx={{ color: '#64748b' }}>Loading...</Typography>}
                    </Stack>

                    <Stack spacing={1.5}>
                        {overrideRows.length === 0 && (
                            <Alert severity="info" variant="outlined">No overrides for this employee and period.</Alert>
                        )}
                        {overrideRows.map(row => {
                            const config = componentMap[row.component_code] || { label: row.component_code, unit: row.unit };
                            return (
                                <Box key={row.id} sx={{ p: 2, border: '1px solid #e2e8f0', borderRadius: 2, bgcolor: '#fff' }}>
                                    <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
                                        <Box>
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <Typography sx={{ fontWeight: 800, color: '#0f172a' }}>{config.label}</Typography>
                                                <Chip size="small" label={row.status} color={statusColor(row.status)} variant="outlined" />
                                            </Stack>
                                            <Typography variant="body2" sx={{ color: '#475569', mt: 0.75 }}>
                                                {formatOverrideValue(row.calculated_value, config.unit)} → {formatOverrideValue(row.override_value, config.unit)}
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mt: 0.75 }}>
                                                {row.reason}
                                            </Typography>
                                        </Box>
                                        <Stack direction="row" spacing={0.5}>
                                            {row.status === 'PENDING' && (
                                                <>
                                                    <Tooltip title="Approve">
                                                        <IconButton size="small" color="success" disabled={overrideSaving} onClick={() => actOnOverride(row, 'approve')}>
                                                            <ApproveIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Reject">
                                                        <IconButton size="small" color="error" disabled={overrideSaving} onClick={() => actOnOverride(row, 'reject')}>
                                                            <RejectIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </>
                                            )}
                                            {row.status === 'APPROVED' && (
                                                <Tooltip title="Revoke">
                                                    <IconButton size="small" color="warning" disabled={overrideSaving} onClick={() => actOnOverride(row, 'revoke')}>
                                                        <RevokeIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </Stack>
                                    </Stack>
                                </Box>
                            );
                        })}
                    </Stack>
                </Box>
            </Drawer>

            <Snackbar
                open={message.open}
                autoHideDuration={5000}
                onClose={() => setMessage({ ...message, open: false })}
            >
                <Alert severity={message.severity} onClose={() => setMessage({ ...message, open: false })} sx={{ width: '100%' }}>
                    {message.text}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default MonthlyReview;
